import type {
  CreateTaskBody,
  Task,
  TaskComment,
  TaskCommentListResult,
  TaskListResult,
  UpdateChecklistItemBody,
  UpdateTaskBody,
} from '@obras/shared';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';

/*
 * Tarefas (ADR 063): uma consulta só, a lista inteira de quem olha, e cada
 * gravação devolve a tarefa como ficou — que toma o lugar dela na lista. O
 * modal lê da mesma lista, então tela e modal nunca discordam.
 */

export const tarefaKeys = {
  lista: ['tarefas'] as const,
  comentarios: (id: string) => ['tarefas', id, 'comentarios'] as const,
};

export function useTarefas() {
  return useQuery({
    queryKey: tarefaKeys.lista,
    queryFn: ({ signal }) => api.get<TaskListResult>('/tarefas', signal),
  });
}

/** A tarefa que o servidor devolveu, no lugar da antiga (ou no alto, se é nova). */
function guardar(queryClient: QueryClient, tarefa: Task): void {
  queryClient.setQueryData<TaskListResult>(tarefaKeys.lista, (atual) =>
    atual ? { items: [tarefa, ...atual.items.filter((item) => item.id !== tarefa.id)] } : atual,
  );
}

function retirar(queryClient: QueryClient, id: string): void {
  queryClient.setQueryData<TaskListResult>(tarefaKeys.lista, (atual) =>
    atual ? { items: atual.items.filter((item) => item.id !== id) } : atual,
  );
}

/**
 * Muda a tarefa na lista antes da resposta — marcar um item ou trocar a
 * situação não espera a rede — e devolve como desfazer, se o servidor recusar.
 */
async function adiantar(
  queryClient: QueryClient,
  id: string,
  mudar: (tarefa: Task) => Task,
): Promise<{ anterior: TaskListResult | undefined }> {
  await queryClient.cancelQueries({ queryKey: tarefaKeys.lista });
  const anterior = queryClient.getQueryData<TaskListResult>(tarefaKeys.lista);
  queryClient.setQueryData<TaskListResult>(tarefaKeys.lista, (atual) =>
    atual ? { items: atual.items.map((item) => (item.id === id ? mudar(item) : item)) } : atual,
  );
  return { anterior };
}

function desfazer(
  queryClient: QueryClient,
  contexto: { anterior: TaskListResult | undefined } | undefined,
) {
  if (contexto?.anterior) queryClient.setQueryData(tarefaKeys.lista, contexto.anterior);
}

export function useCriarTarefa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTaskBody) => api.post<Task>('/tarefas', body),
    onSuccess: (tarefa) => guardar(queryClient, tarefa),
  });
}

export function useAtualizarTarefa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateTaskBody & { id: string }) =>
      api.patch<Task>(`/tarefas/${id}`, body),
    onMutate: ({ id, ...body }) =>
      adiantar(queryClient, id, (tarefa) => ({
        ...tarefa,
        ...(body.title !== undefined ? { title: body.title } : {}),
        ...(body.description !== undefined ? { description: body.description || null } : {}),
        ...(body.urgency !== undefined ? { urgency: body.urgency } : {}),
        ...(body.dueAt !== undefined ? { dueAt: body.dueAt } : {}),
        ...(body.status !== undefined
          ? {
              status: body.status,
              completedAt:
                body.status === 'concluida'
                  ? (tarefa.completedAt ?? new Date().toISOString())
                  : null,
            }
          : {}),
      })),
    onError: (_erro, _variaveis, contexto) => desfazer(queryClient, contexto),
    onSuccess: (tarefa) => guardar(queryClient, tarefa),
  });
}

export function useExcluirTarefa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/tarefas/${id}`),
    onSuccess: (_resposta, id) => retirar(queryClient, id),
  });
}

export function useIncluirPessoa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      api.post<Task>(`/tarefas/${id}/membros`, { userId }),
    onSuccess: (tarefa) => guardar(queryClient, tarefa),
  });
}

/** Tirar a si mesmo responde sem a tarefa: ela sai da lista de quem saiu. */
export function useTirarPessoa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      api.delete<Task | undefined>(`/tarefas/${id}/membros/${userId}`),
    onSuccess: (tarefa, { id }) =>
      tarefa ? guardar(queryClient, tarefa) : retirar(queryClient, id),
  });
}

export function useAcrescentarItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      api.post<Task>(`/tarefas/${id}/checklist`, { content }),
    onSuccess: (tarefa) => guardar(queryClient, tarefa),
  });
}

export function useAlterarItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      itemId,
      ...body
    }: UpdateChecklistItemBody & { id: string; itemId: string }) =>
      api.patch<Task>(`/tarefas/${id}/checklist/${itemId}`, body),
    onMutate: ({ id, itemId, ...body }) =>
      adiantar(queryClient, id, (tarefa) => ({
        ...tarefa,
        checklist: tarefa.checklist.map((item) =>
          item.id === itemId
            ? {
                ...item,
                ...(body.content !== undefined ? { content: body.content } : {}),
                ...(body.isDone !== undefined ? { isDone: body.isDone } : {}),
              }
            : item,
        ),
      })),
    onError: (_erro, _variaveis, contexto) => desfazer(queryClient, contexto),
    onSuccess: (tarefa) => guardar(queryClient, tarefa),
  });
}

export function useTirarItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, itemId }: { id: string; itemId: string }) =>
      api.delete<Task>(`/tarefas/${id}/checklist/${itemId}`),
    onMutate: ({ id, itemId }) =>
      adiantar(queryClient, id, (tarefa) => ({
        ...tarefa,
        checklist: tarefa.checklist.filter((item) => item.id !== itemId),
      })),
    onError: (_erro, _variaveis, contexto) => desfazer(queryClient, contexto),
    onSuccess: (tarefa) => guardar(queryClient, tarefa),
  });
}

// ─────────────────────────────────────────────────────────── comentários

/**
 * A conversa da tarefa, lida ao abrir o modal. Com ele aberto, ela se renova a
 * cada 30 segundos: quem está conversando vê o que chega sem fechar e abrir.
 */
export function useComentarios(taskId: string) {
  return useQuery({
    queryKey: tarefaKeys.comentarios(taskId),
    queryFn: ({ signal }) =>
      api.get<TaskCommentListResult>(`/tarefas/${taskId}/comentarios`, signal),
    refetchInterval: 30_000,
  });
}

/** A contagem da linha acompanha a conversa, sem reler a lista. */
function contarComentario(queryClient: QueryClient, taskId: string, delta: number): void {
  queryClient.setQueryData<TaskListResult>(tarefaKeys.lista, (atual) =>
    atual
      ? {
          items: atual.items.map((tarefa) =>
            tarefa.id === taskId
              ? { ...tarefa, commentCount: Math.max(0, tarefa.commentCount + delta) }
              : tarefa,
          ),
        }
      : atual,
  );
}

export function useComentar(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      api.post<TaskComment>(`/tarefas/${taskId}/comentarios`, { content }),
    onSuccess: (comentario) => {
      queryClient.setQueryData<TaskCommentListResult>(tarefaKeys.comentarios(taskId), (atual) => ({
        items: [...(atual?.items ?? []).filter((item) => item.id !== comentario.id), comentario],
      }));
      contarComentario(queryClient, taskId, 1);
    },
  });
}

export function useReescreverComentario(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      api.patch<TaskComment>(`/tarefas/${taskId}/comentarios/${id}`, { content }),
    onSuccess: (comentario) =>
      queryClient.setQueryData<TaskCommentListResult>(tarefaKeys.comentarios(taskId), (atual) =>
        atual
          ? { items: atual.items.map((item) => (item.id === comentario.id ? comentario : item)) }
          : atual,
      ),
  });
}

export function useExcluirComentario(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/tarefas/${taskId}/comentarios/${id}`),
    onSuccess: (_resposta, id) => {
      queryClient.setQueryData<TaskCommentListResult>(tarefaKeys.comentarios(taskId), (atual) =>
        atual ? { items: atual.items.filter((item) => item.id !== id) } : atual,
      );
      contarComentario(queryClient, taskId, -1);
    },
  });
}
